import { StoreRole } from '@app/shared';
import { createBrowserRouter } from 'react-router-dom';
import { ErrorPage } from '@/pages/shared/error';
import {
  GuestOnly,
  RequireAuth,
  RequireMembership,
  RequireSuperAdmin,
  RootRedirect,
} from './guards';
import { AuthLayout } from './layouts';
import { loadNamespace } from '@/shared/i18n/loadNamespace';

const STAFF_ROLES = [StoreRole.OWNER, StoreRole.ADMIN, StoreRole.MANAGER];

export const router = createBrowserRouter([
  {
    path: '/',
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <RootRedirect /> },

      // /login, /register, /forgot-password, /reset-password — redirect away if already signed in.
      {
        element: <GuestOnly />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              {
                path: 'login',
                lazy: async () => {
                  const { LoginPage } = await import('@/pages/auth/login');
                  return { Component: LoginPage };
                },
              },
              {
                path: 'register',
                lazy: async () => {
                  const { RegisterPage } = await import('@/pages/auth/register');
                  return { Component: RegisterPage };
                },
              },
              {
                path: 'forgot-password',
                lazy: async () => {
                  const { ForgotPasswordPage } = await import('@/pages/auth/forgot-password');
                  return { Component: ForgotPasswordPage };
                },
              },
              {
                path: 'reset-password',
                lazy: async () => {
                  const { ResetPasswordPage } = await import('@/pages/auth/reset-password');
                  return { Component: ResetPasswordPage };
                },
              },
            ],
          },
        ],
      },

      // Every authenticated zone.
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              {
                path: 'verify',
                lazy: async () => {
                  const { VerifyPage } = await import('@/pages/auth/verify');
                  return { Component: VerifyPage };
                },
              },
            ],
          },
          {
            path: 'app',
            children: [
              {
                path: 'join',
                lazy: async () => {
                  const { JoinStorePage } = await import('@/pages/customer/join-store');
                  return { Component: JoinStorePage };
                },
              },
              {
                path: 'pending',
                lazy: async () => {
                  const { PendingApprovalPage } = await import('@/pages/customer/pending-approval');
                  return { Component: PendingApprovalPage };
                },
              },
              {
                element: <RequireMembership roles={[StoreRole.CUSTOMER]} fallback="join" />,
                children: [
                  {
                    lazy: async () => {
                      // Both, not just 'client' — order/payment/claim status labels live in
                      // 'client' and are read from admin screens too (translateOrderStatus etc.),
                      // so a customer session needs 'admin' loaded wherever it might render one.
                      const [{ CustomerLayout }] = await Promise.all([
                        import('./layouts/CustomerLayout'),
                        loadNamespace('client'),
                        loadNamespace('admin'),
                      ]);
                      return { Component: CustomerLayout };
                    },
                    children: [
                      {
                        index: true,
                        lazy: async () => {
                          const { CustomerHomePage } = await import('@/pages/customer/home');
                          return { Component: CustomerHomePage };
                        },
                      },
                      {
                        path: 'category/:categoryId',
                        lazy: async () => {
                          const { CategoryPage } =
                            await import('@/pages/customer/catalog/category');
                          return { Component: CategoryPage };
                        },
                      },
                      {
                        path: 'search',
                        lazy: async () => {
                          const { SearchPage } = await import('@/pages/customer/catalog/search');
                          return { Component: SearchPage };
                        },
                      },
                      {
                        path: 'product/:productId',
                        lazy: async () => {
                          const { ProductPage } = await import('@/pages/customer/catalog/product');
                          return { Component: ProductPage };
                        },
                      },
                      {
                        path: 'cart',
                        lazy: async () => {
                          const { CartPage } = await import('@/pages/customer/cart');
                          return { Component: CartPage };
                        },
                      },
                      {
                        path: 'checkout',
                        lazy: async () => {
                          const { CheckoutPage } = await import('@/pages/customer/checkout');
                          return { Component: CheckoutPage };
                        },
                      },
                      {
                        path: 'orders',
                        lazy: async () => {
                          const { OrdersListPage } = await import('@/pages/customer/orders/list');
                          return { Component: OrdersListPage };
                        },
                      },
                      {
                        path: 'orders/:orderId',
                        lazy: async () => {
                          const { OrderDetailPage } =
                            await import('@/pages/customer/orders/detail');
                          return { Component: OrderDetailPage };
                        },
                      },
                      {
                        path: 'claims',
                        lazy: async () => {
                          const { ClaimsListPage } = await import('@/pages/customer/claims/list');
                          return { Component: ClaimsListPage };
                        },
                      },
                      {
                        path: 'claims/:claimId',
                        lazy: async () => {
                          const { ClaimDetailPage } =
                            await import('@/pages/customer/claims/detail');
                          return { Component: ClaimDetailPage };
                        },
                      },
                      {
                        path: 'templates',
                        lazy: async () => {
                          const { TemplatesPage } = await import('@/pages/customer/templates');
                          return { Component: TemplatesPage };
                        },
                      },
                      {
                        path: 'favorites',
                        lazy: async () => {
                          const { FavoritesPage } = await import('@/pages/customer/favorites');
                          return { Component: FavoritesPage };
                        },
                      },
                      {
                        path: 'balance',
                        lazy: async () => {
                          const { BalancePage } = await import('@/pages/customer/balance');
                          return { Component: BalancePage };
                        },
                      },
                      {
                        path: 'profile',
                        lazy: async () => {
                          const { ProfilePage } = await import('@/pages/customer/profile');
                          return { Component: ProfilePage };
                        },
                      },
                      {
                        path: 'profile/addresses',
                        lazy: async () => {
                          const { AddressesPage } =
                            await import('@/pages/customer/profile/addresses');
                          return { Component: AddressesPage };
                        },
                      },
                      {
                        path: 'profile/notifications',
                        lazy: async () => {
                          const { NotificationSettingsPage } =
                            await import('@/pages/customer/profile/notifications');
                          return { Component: NotificationSettingsPage };
                        },
                      },
                      {
                        path: 'help',
                        lazy: async () => {
                          const { HelpPage } = await import('@/pages/customer/help');
                          return { Component: HelpPage };
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: 'admin',
            element: <RequireMembership roles={STAFF_ROLES} />,
            children: [
              {
                lazy: async () => {
                  // Both, not just 'admin' — see the matching comment on the customer loader:
                  // shared status/reason labels (order/claim) live in 'client' and get read from
                  // admin screens too (translateOrderStatus/translateClaimStatus etc.).
                  const [{ AdminLayout }] = await Promise.all([
                    import('./layouts/AdminLayout'),
                    loadNamespace('admin'),
                    loadNamespace('client'),
                  ]);
                  return { Component: AdminLayout };
                },
                children: [
                  {
                    index: true,
                    lazy: async () => {
                      const { AdminDashboardPage } = await import('@/pages/admin/dashboard');
                      return { Component: AdminDashboardPage };
                    },
                  },
                  {
                    path: 'requests',
                    lazy: async () => {
                      const { JoinRequestsPage } = await import('@/pages/admin/requests');
                      return { Component: JoinRequestsPage };
                    },
                  },
                  {
                    path: 'customers',
                    lazy: async () => {
                      const { CustomersListPage } = await import('@/pages/admin/customers/list');
                      return { Component: CustomersListPage };
                    },
                  },
                  {
                    path: 'customers/:id',
                    lazy: async () => {
                      const { CustomerDetailPage } = await import('@/pages/admin/customers/detail');
                      return { Component: CustomerDetailPage };
                    },
                  },
                  {
                    path: 'staff',
                    lazy: async () => {
                      const { StaffPage } = await import('@/pages/admin/staff');
                      return { Component: StaffPage };
                    },
                  },
                  {
                    path: 'categories',
                    lazy: async () => {
                      const { CategoriesPage } = await import('@/pages/admin/categories');
                      return { Component: CategoriesPage };
                    },
                  },
                  {
                    path: 'products',
                    lazy: async () => {
                      const { ProductsListPage } = await import('@/pages/admin/products/list');
                      return { Component: ProductsListPage };
                    },
                  },
                  {
                    path: 'products/new',
                    lazy: async () => {
                      const { ProductFormPage } = await import('@/pages/admin/products/form');
                      return { Component: ProductFormPage };
                    },
                  },
                  {
                    path: 'products/:id',
                    lazy: async () => {
                      const { ProductFormPage } = await import('@/pages/admin/products/form');
                      return { Component: ProductFormPage };
                    },
                  },
                  {
                    path: 'units',
                    lazy: async () => {
                      const { UnitsPage } = await import('@/pages/admin/units');
                      return { Component: UnitsPage };
                    },
                  },
                  {
                    path: 'price-types',
                    lazy: async () => {
                      const { PriceTypesPage } = await import('@/pages/admin/price-types');
                      return { Component: PriceTypesPage };
                    },
                  },
                  {
                    path: 'discounts',
                    lazy: async () => {
                      const { DiscountsPage } = await import('@/pages/admin/discounts');
                      return { Component: DiscountsPage };
                    },
                  },
                  {
                    path: 'import',
                    lazy: async () => {
                      const { ImportPage } = await import('@/pages/admin/import');
                      return { Component: ImportPage };
                    },
                  },
                  {
                    path: 'settings',
                    lazy: async () => {
                      const { SettingsPage } = await import('@/pages/admin/settings');
                      return { Component: SettingsPage };
                    },
                  },
                  {
                    path: 'orders',
                    lazy: async () => {
                      const { OrderBoardPage } = await import('@/pages/admin/orders/board');
                      return { Component: OrderBoardPage };
                    },
                  },
                  {
                    path: 'orders/new',
                    lazy: async () => {
                      const { CreateOrderPage } = await import('@/pages/admin/orders/create');
                      return { Component: CreateOrderPage };
                    },
                  },
                  {
                    path: 'orders/:id',
                    lazy: async () => {
                      const { OrderDetailPage } = await import('@/pages/admin/orders/detail');
                      return { Component: OrderDetailPage };
                    },
                  },
                  {
                    path: 'stock',
                    lazy: async () => {
                      const { StockLevelsPage } = await import('@/pages/admin/stock/levels');
                      return { Component: StockLevelsPage };
                    },
                  },
                  {
                    path: 'stock/batches',
                    lazy: async () => {
                      const { BatchesPage } = await import('@/pages/admin/stock/batches');
                      return { Component: BatchesPage };
                    },
                  },
                  {
                    path: 'stock/products/:id',
                    lazy: async () => {
                      const { StockProductPage } = await import('@/pages/admin/stock/product');
                      return { Component: StockProductPage };
                    },
                  },
                  {
                    path: 'documents',
                    lazy: async () => {
                      const { DocumentsListPage } = await import('@/pages/admin/documents/list');
                      return { Component: DocumentsListPage };
                    },
                  },
                  {
                    path: 'documents/new',
                    lazy: async () => {
                      const { DocumentFormPage } = await import('@/pages/admin/documents/form');
                      return { Component: DocumentFormPage };
                    },
                  },
                  {
                    path: 'documents/:id',
                    lazy: async () => {
                      const { DocumentFormPage } = await import('@/pages/admin/documents/form');
                      return { Component: DocumentFormPage };
                    },
                  },
                  {
                    path: 'counterparties',
                    lazy: async () => {
                      const { CounterpartiesListPage } =
                        await import('@/pages/admin/counterparties/list');
                      return { Component: CounterpartiesListPage };
                    },
                  },
                  {
                    path: 'counterparties/:id',
                    lazy: async () => {
                      const { CounterpartyDetailPage } =
                        await import('@/pages/admin/counterparties/detail');
                      return { Component: CounterpartyDetailPage };
                    },
                  },
                  {
                    path: 'claims',
                    lazy: async () => {
                      const { ClaimsListPage } = await import('@/pages/admin/claims/list');
                      return { Component: ClaimsListPage };
                    },
                  },
                  {
                    path: 'claims/:claimId',
                    lazy: async () => {
                      const { ClaimDetailPage } = await import('@/pages/admin/claims/detail');
                      return { Component: ClaimDetailPage };
                    },
                  },
                  {
                    path: 'payments',
                    lazy: async () => {
                      const { PaymentsPage } = await import('@/pages/admin/payments');
                      return { Component: PaymentsPage };
                    },
                  },
                  {
                    path: 'reports',
                    lazy: async () => {
                      const { ReportsPage } = await import('@/pages/admin/reports');
                      return { Component: ReportsPage };
                    },
                  },
                  {
                    path: 'audit',
                    lazy: async () => {
                      const { AuditLogPage } = await import('@/pages/admin/audit');
                      return { Component: AuditLogPage };
                    },
                  },
                ],
              },
            ],
          },
          {
            path: 'courier',
            element: <RequireMembership roles={[StoreRole.COURIER]} />,
            children: [
              {
                lazy: async () => {
                  // 'client' is needed for translatePaymentMethod/translateOrderStatus, used on
                  // the deliveries list alongside the courier-specific 'courier' namespace.
                  const [{ CourierLayout }] = await Promise.all([
                    import('./layouts/CourierLayout'),
                    loadNamespace('courier'),
                    loadNamespace('client'),
                  ]);
                  return { Component: CourierLayout };
                },
                children: [
                  {
                    index: true,
                    lazy: async () => {
                      const { CourierHomePage } = await import('@/pages/courier/home');
                      return { Component: CourierHomePage };
                    },
                  },
                ],
              },
            ],
          },
          {
            path: 'platform',
            element: <RequireSuperAdmin />,
            children: [
              {
                lazy: async () => {
                  const [{ PlatformLayout }] = await Promise.all([
                    import('./layouts/PlatformLayout'),
                    loadNamespace('platform'),
                  ]);
                  return { Component: PlatformLayout };
                },
                children: [
                  {
                    index: true,
                    lazy: async () => {
                      const { StoresListPage } = await import('@/pages/platform/stores/list');
                      return { Component: StoresListPage };
                    },
                  },
                  {
                    path: 'stores/:id',
                    lazy: async () => {
                      const { StoreDetailPage } = await import('@/pages/platform/stores/detail');
                      return { Component: StoreDetailPage };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },

      {
        path: 'join/:code',
        lazy: async () => {
          const { JoinLinkPage } = await import('@/pages/shared/join-link');
          return { Component: JoinLinkPage };
        },
      },
      {
        path: 'no-access',
        lazy: async () => {
          const { NoAccessPage } = await import('@/pages/shared/no-access');
          return { Component: NoAccessPage };
        },
      },
      {
        path: '*',
        lazy: async () => {
          const { NotFoundPage } = await import('@/pages/shared/not-found');
          return { Component: NotFoundPage };
        },
      },
    ],
  },
]);
